import React, { useEffect } from "react";
import { Styled } from "../styles";
import {
    Box,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    Stack,
    Typography,
    Button,
    InputLabel,
    Tooltip,
} from "@mui/material";
import { useDownloadQRCode, useQRCode } from "./hooks";
import { useModal } from "@/components/product/table/modal/context";
import { isFoodAndDrinks } from "@/utils/validations";

const QRCodeModal: React.FC = () => {
    const product = useModal(state => state.product);
    const typeName = product?.typeName;

    const onDownloadQRCode = useDownloadQRCode();

    const {
        canvasRef,
        loading,
        getQRCode,
        settings,
        onChangeSettings,
        drawCanvas,
        parsedSvg,
        setParsedSvg,
        addTextToSVG,
    } = useQRCode();

    const { kind, format, color, showInfo, showEnergy } = settings;
    const isDark = color === "dark";

    const generateQRCode = async () => {
        const qrString = await getQRCode({ format, color, kind });
        if (format === "png") {
            drawCanvas(settings, qrString!);
        } else {
            const svgStringWithText = addTextToSVG(settings, qrString!);
            setParsedSvg(svgStringWithText);
        }
    };

    useEffect(() => {
        generateQRCode();
    }, []);

    return (
        <Styled.QRBody>
            <Box>
                <Styled.QRWrapper bgcolor={`${isDark ? "white" : "black"}`} data-testid="qr-code">
                    <CircularProgress
                        sx={{ margin: "auto", display: loading ? "block" : "none" }}
                    />
                    <Styled.QRCanvas
                        width={250}
                        height={250}
                        ref={canvasRef}
                        style={{ display: loading || format === "svg" ? "none" : "block" }}
                    />
                    <Styled.QRSvg sx={{ display: loading || format === "png" ? "none" : "block" }}>
                        {parsedSvg && <div dangerouslySetInnerHTML={{ __html: parsedSvg }} />}
                    </Styled.QRSvg>
                </Styled.QRWrapper>
                {settings.kind === "marketing" ? (
                    <Styled.ComplianceAlert icon={false} severity="warning">
                        This QR-code directs to the marketing version of the product, and is not
                        compliant as the only QR-code on a product.
                    </Styled.ComplianceAlert>
                ) : null}
            </Box>
            <Styled.QRSettings>
                <Box display="flex" flexDirection="column" gap="24px">
                    <FormControl data-testid="kind">
                        <InputLabel>Choose type</InputLabel>
                        <Select
                            label="Choose type"
                            onChange={onChangeSettings}
                            value={kind}
                            name="kind"
                        >
                            <MenuItem value="eucompliant" data-testid="eucompliant">
                                EU Compliant
                                <Typography
                                    marginLeft="5px"
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    (Best for compliance with the EU regulations)
                                </Typography>
                            </MenuItem>
                            <MenuItem value="marketing" data-testid="marketing">
                                Marketing
                                <Typography
                                    marginLeft="5px"
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    (Best for advertising)
                                </Typography>
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl data-testid="format">
                        <InputLabel>Choose format</InputLabel>
                        <Select
                            label="Choose format"
                            onChange={onChangeSettings}
                            value={format}
                            name="format"
                        >
                            <MenuItem value="svg" data-testid="svg">
                                SVG{" "}
                                <Typography
                                    marginLeft="5px"
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    (Best for printing)
                                </Typography>
                            </MenuItem>

                            <MenuItem value="png" data-testid="png">
                                PNG
                                <Typography
                                    marginLeft="5px"
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    (Best for viewing and sharing)
                                </Typography>
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl data-testid="color">
                        <InputLabel>Color</InputLabel>
                        <Select
                            label="Color"
                            onChange={onChangeSettings}
                            value={color}
                            name="color"
                        >
                            <MenuItem value="dark" data-testid="dark">
                                Dark QR code
                                <Typography
                                    marginLeft="5px"
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    (Best for light-colored label)
                                </Typography>
                            </MenuItem>

                            <MenuItem value="light" data-testid="light">
                                Light QR code
                                <Typography
                                    marginLeft="5px"
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    (Best for dark-colored label)
                                </Typography>
                            </MenuItem>
                        </Select>
                    </FormControl>
                    {isFoodAndDrinks(typeName) ? (
                        <Stack display="flex" flexDirection="column" alignItems="flex-start">
                            <Typography marginBottom="5px" fontWeight="bold">
                                Additions
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        sx={{ padding: "2px 9px" }}
                                        disableRipple
                                        name="showInfo"
                                        checked={showInfo}
                                        onChange={onChangeSettings}
                                    />
                                }
                                data-testid="showInfo"
                                label={`Display ${kind === "eucompliant" ? "ingredients" : "marketing"}`}
                            />
                            <Tooltip
                                title={
                                    !product?.nutrition
                                        ? "This product is missing nutritional information"
                                        : null
                                }
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            sx={{ padding: "2px 9px" }}
                                            disableRipple
                                            name="showEnergy"
                                            disabled={kind === "marketing" || !product?.nutrition}
                                            checked={showEnergy}
                                            onChange={onChangeSettings}
                                        />
                                    }
                                    data-testid="showEnergy"
                                    label="Display energy"
                                />
                            </Tooltip>
                        </Stack>
                    ) : null}
                </Box>
                <Button
                    data-testid="download-qr-code"
                    type="submit"
                    onClick={() => onDownloadQRCode({ canvasRef, parsedSvg, ...settings })}
                >
                    Download
                </Button>
            </Styled.QRSettings>
        </Styled.QRBody>
    );
};

export default QRCodeModal;
